import { type ReactNode } from 'react';

import { Typography } from '@/design-system';
import { type CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { SendConfirmation } from './send-confirmation';
import { SendFlowView } from './send-flow-view';
import { SendForm } from './send-form';
import { SendErrorScreen, SendLoadingScreen, SendSuccessScreen } from './send-status-screen';
import { type SendFlowViewModel } from './use-send-flow';

interface SendFlowBodyProps {
  view: SendFlowView;
  flow: SendFlowViewModel;
  assets: CryptoAsset[];
  verification: ReactNode;
}

interface SendDraftProps {
  flow: SendFlowViewModel;
  assets: CryptoAsset[];
}

export const SendFlowBody = ({ view, flow, assets, verification }: SendFlowBodyProps) => {
  switch (view) {
    case SendFlowView.Verification:
      return verification;
    case SendFlowView.Suggestion:
      return (
        <Typography variant="body3">
          Add an optional authenticator for extra wallet security, or continue with this transfer.
        </Typography>
      );
    case SendFlowView.Processing:
      return <SendLoadingScreen />;
    case SendFlowView.Success:
      return (
        <SendSuccessScreen
          amount={flow.amount}
          symbol={flow.coin?.symbol ?? ''}
          digest={flow.digest}
        />
      );
    case SendFlowView.Uncertain:
      return (
        <Typography variant="body3" role="status">
          {flow.error}
        </Typography>
      );
    case SendFlowView.Locked:
      return (
        <Typography variant="body3">
          Unlock your wallet with a passkey to send. Your transfer details will stay here.
        </Typography>
      );
    case SendFlowView.Failed:
      return <SendErrorScreen message={flow.error} />;
    case SendFlowView.Confirmation:
    case SendFlowView.Form:
      return <SendDraft flow={flow} assets={assets} />;
  }
};

const SendDraft = ({ flow, assets }: SendDraftProps) => {
  const { coin, confirmationRecipient } = flow;

  if (!coin) {
    return null;
  }

  if (confirmationRecipient) {
    return (
      <SendConfirmation
        coin={coin}
        amount={flow.amount}
        amountUsd={flow.amountUsd}
        balanceLabel={flow.balanceLabel}
        confirmationRecipient={confirmationRecipient}
      />
    );
  }

  return (
    <SendForm
      assets={assets}
      coin={coin}
      amount={flow.amount}
      amountUsd={flow.amountUsd}
      amountError={flow.amountError}
      recipientInput={flow.recipientInput}
      selectedRecipient={flow.selectedRecipient}
      invalidRecipient={flow.invalidRecipient}
      recipientHelper={flow.recipientHelper}
      disabledReason={flow.disabledReason}
      onAmountChange={flow.setAmount}
      onCoinChange={flow.setCoinType}
      onRecipientInputChange={flow.setRecipientInput}
      onRecipientResolve={flow.resolveRecipientAddress}
      onRecipientSelect={flow.selectRecipient}
      onRecipientIdentityEnrich={flow.enrichRecipientIdentity}
      onRecipientEdit={flow.editRecipient}
      onRecipientClear={flow.clearRecipient}
      onRecipientBlur={flow.handleRecipientBlur}
    />
  );
};
